import React from "react";
import {
  InsightContainer,
  Title,
  InsightContent,
  ListItem,
  ErrorWrapper,
  EmptyWrapper,
} from "./Insight.styled";
import { Typography, Card, WidgetEmptyErrorPlaceholder } from "@ann/web-components";
import { Icon } from "@ann/web-components";
import ExpandableChilds from "../ExpandableChilds/ExpandableChilds";

/**
 * Extract displayable text from an item that could be a string or an object
 * @param {string|object} item - The item to extract text from
 * @returns {string} The displayable text
 */
const getItemText = (item) => {
  if (typeof item === "string") return item;
  if (item && typeof item === "object") {
    return item.title || item.content || item.description || JSON.stringify(item);
  }
  return String(item);
};

const Insight = ({
  hasData = false,
  loading = false,
  error = null,
  onRefresh,
  summary = null,
  opportunities = [],
  recommendations = [],
  riskFactors = [],
}) => {
  if (error) {
    return (
      <InsightContainer>
        <ErrorWrapper>
          <WidgetEmptyErrorPlaceholder
            icon="error"
            title="Something went wrong"
            message="We couldn't load snapshot insights. Please try again."
            buttonText="Retry"
            showButton={true}
            onClick={onRefresh}
          />
        </ErrorWrapper>
      </InsightContainer>
    );
  }

  if (!hasData && !loading) {
    return (
      <InsightContainer>
        <EmptyWrapper>
          <WidgetEmptyErrorPlaceholder
            icon="searchOff"
            title="No data to display here yet"
            message="We'll display behavioural and preference data once there's enough interaction across calls, messages, or notes."
            showButton={false}
          />
        </EmptyWrapper>
      </InsightContainer>
    );
  }

  return (
    <InsightContainer>
      {summary && (
        <>
          <Title>
            <Icon name="aiChat" color="primary" />
            <Typography category="subtitle" size="medium" weight="bold">
              Summary
            </Typography>
          </Title>

          <Typography category="body" size="medium" weight="regular">
            {summary}
          </Typography>
        </>
      )}

      <InsightContent>
        {opportunities.length > 0 && (
          <Card width="100%" onClick={() => {}} size="large">
            <Title>
              <Icon name="lightbulb2" color="#2173FF" />
              <Typography category="subtitle" size="medium" weight="bold">
                Opportunities
              </Typography>
            </Title>

            <ExpandableChilds renderChilds={3}>
              {opportunities.map((item, index) => (
                <ListItem key={index} role="listitem">
                  {getItemText(item)}
                </ListItem>
              ))}
            </ExpandableChilds>
          </Card>
        )}

        {recommendations.length > 0 && (
          <Card width="100%" onClick={() => {}} size="large">
            <Title>
              <Icon name="star" color="#00A07E" />
              <Typography category="subtitle" size="medium" weight="bold">
                Recommendations
              </Typography>
            </Title>
            <ExpandableChilds renderChilds={2}>
              {recommendations.map((item, index) => (
                <ListItem key={index} role="listitem">
                  {getItemText(item)}
                </ListItem>
              ))}
            </ExpandableChilds>
          </Card>
        )}

        {riskFactors.length > 0 && (
          <Card width="100%" onClick={() => {}} size="large">
            <Title>
              <Icon name="warning" color="#992222" />
              <Typography category="subtitle" size="medium" weight="bold">
                Risk Factors
              </Typography>
            </Title>
            <ExpandableChilds renderChilds={2}>
              {riskFactors.map((item, index) => (
                <ListItem key={index} role="listitem">
                  {getItemText(item)}
                </ListItem>
              ))}
            </ExpandableChilds>
          </Card>
        )}
      </InsightContent>
    </InsightContainer>
  );
};

export default Insight;
